/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import Modal from '../../../../components/Modal'
import { useServiceContext } from '../../../../root'
import { RcmTableDataContext } from '../../../../components/rcm-table/RcmTableDataContext'
import EditForm from './EditForm'
import { toast } from '../../../../services/toast'
import { documentTypesApiService } from '../../../../services/api/api.documenttypes'
import { getSignatureFormsList, getFiles, getFilesNoVersion } from '../../../../services/api/files'
import { SYSTEM_FOLDER_TYPES } from '../../../../services/auth'
import ValidationSchema from './EditFormValidation'
import { COMPLETE_STATUSES } from './EditFormConstants'
import cloneDeep from 'lodash/cloneDeep'

interface ModalEditProps {
  documentType?: any
  onClose: () => void
  onSuccess?: () => void
}

const ModalEdit: React.FC<ModalEditProps> = ({ documentType, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableContext = useContext(RcmTableDataContext)

  const [isEdit, setIsEdit] = useState(!!documentType)
  const [isOpen, setIsOpen] = useState(true)
  const [loading, setLoading] = useState<Promise<any> | null>(null)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [documentTypeData, setDocumentTypeData] = useState<any>({
    name: '',
    description: '',
    optionalMetadataTemplates: [],
    requiredMetadataTemplates: [],
    requireExpirationDate: false,
    watermarkIds: [],
    templateFileId: null,
    signatureTemplateFileId: null,
    addSignaturePageToFront: false,
    workflowTemplate: null,
    _customEffectiveStateLabelType: 'effective',
    _customEffectiveStateLabelText: '',
    effectiveTimeDaysEnabled: false,
    effectiveTimeDays: 10,
    expirationTimeMonthsEnabled: false,
    isPeriodicReviewCustomDateEnabled: false,
    customLastDateMetadataTemplate: null,
    customNextDateMetadataTemplate: null,
    expirationTimeMonths: 24,
    autonamingEnabled: false,
    autonamingSeqNo: '0',
    autonamingTemplate: '',
    keepDraftVersionsAfterApproval: false,
    allowBlindingStatusChange: false,
    isInitiatorReviewerOverlapCheckEnabled: true,
    dueDateEnabled: false,
    dueDateOffset: 30,
    amendmentsEnabled: false,
    allowPropertiesChangeDuringEdit: true,
    allowPropertiesChangeDuringReview: false,
    requireJustification: false,
    requireRejectReason: false,
    allowOverridesDuringCR: false
  })
  const [templatesFiles, setTemplatesFiles] = useState<any[]>([])
  const [availableSignatureForms, setAvailableSignatureForms] = useState<any[]>([])

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoadingData(true)
    try {
      // If editing, load the document type data
      if (isEdit && documentType) {
        const documentTypeId = documentType?.value ?? documentType?.id ?? null
        const docTypeData = await documentTypesApiService.getById(documentTypeId, false)

        // Transform API response to form structure
        const formData = transformDocTypeToFormData(docTypeData)
        setDocumentTypeData(formData)
      }

      // Load signature forms and system folders in parallel
      const [signatureForms, systemFoldersResponse] = await Promise.all([
        getSignatureFormsList({ sortBy: 'fileName', sortDesc: false }),
        getFilesNoVersion(0, { obsolete: false, systemFolderType: SYSTEM_FOLDER_TYPES.TEMPLATES })
      ])

      const signatureFormOptions = signatureForms.map((form: any) => ({
        value: form.id,
        label: form.fileName || form.name
      }))
      setAvailableSignatureForms(signatureFormOptions)

      // Find the TEMPLATES folder from children.elements
      const systemFolders = systemFoldersResponse as any
      if (systemFolders?.children && systemFolders.children.elements) {
        const templatesFolder = systemFolders.children.elements.find(
          (folder: any) => folder.systemFolderType === SYSTEM_FOLDER_TYPES.TEMPLATES
        )

        if (templatesFolder) {
          // Get files inside the TEMPLATES folder
          const templatesFolderContents: any = await getFilesNoVersion(templatesFolder.id, {
            sortBy: 'name',
            sortDesc: false,
            obsolete: false
          })

          // Filter for DOCUMENT type files from children.elements
          if (templatesFolderContents?.children && templatesFolderContents.children.elements) {
            const documentFiles = templatesFolderContents.children.elements.filter(
              (item: any) => item.type === 'DOCUMENT'
            )

            const templateOptions = documentFiles.map((file: any) => ({
              value: file.id,
              label: file.fileName || file.name
            }))
            setTemplatesFiles(templateOptions)
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load template and signature form data. Please try again.')
    } finally {
      setIsLoadingData(false)
    }
  }

  const transformDocTypeToFormData = (apiData: any) => {
    // Transform API response to match form structure
    // For dropdown fields, we need to transform IDs/objects to {value, label} format

    return {
      id: apiData.id,
      name: apiData.name || '',
      description: apiData.description || '',
      // Transform metadata templates to {value, label} format
      optionalMetadataTemplates: Array.isArray(apiData.optionalMetadataTemplates)
        ? apiData.optionalMetadataTemplates.map((item: any) => ({
            value: item.id || item.value || item,
            label: item.name || item.label || item.toString(),
            ...item
          }))
        : [],
      requiredMetadataTemplates: Array.isArray(apiData.requiredMetadataTemplates)
        ? apiData.requiredMetadataTemplates.map((item: any) => ({
            value: item.id || item.value || item,
            label: item.name || item.label || item.toString(),
            ...item
          }))
        : [],
      requireExpirationDate: !!apiData.requireExpirationDate,
      // Transform watermarks array to {value, label} format
      watermarkIds: Array.isArray(apiData.watermarks)
        ? apiData.watermarks.map((item: any) => ({
            value: item.id || item.value,
            label: item.name || item.label || item.toString(),
            ...item
          }))
        : [],
      // Transform templateFile object to {value, label} format
      templateFileId: apiData.templateFile
        ? {
            value: apiData.templateFile.id,
            label: apiData.templateFile.name || apiData.templateFile.fileName
          }
        : null,
      // Transform signatureTemplateFile object to {value, label} format
      signatureTemplateFileId: apiData.signatureTemplateFile
        ? {
            value: apiData.signatureTemplateFile.id,
            label: apiData.signatureTemplateFile.name || apiData.signatureTemplateFile.fileName
          }
        : null,
      addSignaturePageToFront: apiData.addSignaturePageToFront === true,
      // Transform workflowTemplate to {value, label} format
      workflowTemplate: apiData.workflowTemplate
        ? {
            value: apiData.workflowTemplate.id,
            label: apiData.workflowTemplate.name
          }
        : null,
      // Map customEffectiveStateLabel from API to _customEffectiveStateLabelType
      // Legacy checks if value matches a known status; if not, it's custom text
      _customEffectiveStateLabelType: (() => {
        const label = apiData.customEffectiveStateLabel || ''
        const predefined = Object.values(COMPLETE_STATUSES).filter((v) => v !== COMPLETE_STATUSES.CUSTOM)
        if (label && !predefined.some((v) => v.toLowerCase() === label.toLowerCase())) {
          return 'custom'
        }
        return label.toLowerCase() || 'effective'
      })(),
      _customEffectiveStateLabelText: (() => {
        const label = apiData.customEffectiveStateLabel || ''
        const predefined = Object.values(COMPLETE_STATUSES).filter((v) => v !== COMPLETE_STATUSES.CUSTOM)
        if (label && !predefined.some((v) => v.toLowerCase() === label.toLowerCase())) {
          return label
        }
        return ''
      })(),
      effectiveTimeDaysEnabled: !!apiData.effectiveTimeDaysEnabled,
      effectiveTimeDays: apiData.effectiveTimeDays || 10,
      expirationTimeMonthsEnabled: !!apiData.expirationTimeMonthsEnabled,
      isPeriodicReviewCustomDateEnabled: !!apiData.isPeriodicReviewCustomDateEnabled,
      // Transform custom date templates to {value, label} format if they're objects
      customLastDateMetadataTemplate: apiData.customLastDateMetadataTemplate
        ? typeof apiData.customLastDateMetadataTemplate === 'object'
          ? { value: apiData.customLastDateMetadataTemplate.id, label: apiData.customLastDateMetadataTemplate.name }
          : apiData.customLastDateMetadataTemplate
        : null,
      customNextDateMetadataTemplate: apiData.customNextDateMetadataTemplate
        ? typeof apiData.customNextDateMetadataTemplate === 'object'
          ? { value: apiData.customNextDateMetadataTemplate.id, label: apiData.customNextDateMetadataTemplate.name }
          : apiData.customNextDateMetadataTemplate
        : null,
      expirationTimeMonths: apiData.expirationTimeMonths || 24,
      autonamingEnabled: !!apiData.autonamingEnabled,
      autonamingSeqNo: apiData.autonamingSeqNo?.toString() || '0',
      autonamingTemplate: apiData.autonamingTemplate || '',
      keepDraftVersionsAfterApproval: !!apiData.keepDraftVersionsAfterApproval,
      allowBlindingStatusChange: apiData.allowBlindingStatusChange === true,
      isInitiatorReviewerOverlapCheckEnabled: apiData.isInitiatorReviewerOverlapCheckEnabled !== false,
      dueDateEnabled: !!apiData.dueDateEnabled,
      dueDateOffset: apiData.dueDateOffset || 30,
      amendmentsEnabled: !!apiData.amendmentsEnabled,
      allowPropertiesChangeDuringEdit: apiData.allowPropertiesChangeDuringEdit !== false,
      allowPropertiesChangeDuringReview: !!apiData.allowPropertiesChangeDuringReview,
      requireJustification: !!apiData.requireJustification,
      requireRejectReason: !!apiData.requireRejectReason,
      allowOverridesDuringCR: !!apiData.allowOverridesDuringCR
    }
  }

  const transformFormDataToApi = (formData: any) => {
    // Transform form data to API structure
    // Extract IDs from {value, label} objects
    // Extract IDs from optional and required metadata templates
    const optionalIds = Array.isArray(formData.optionalMetadataTemplates)
      ? formData.optionalMetadataTemplates.map((item: any) => (typeof item === 'object' ? item.value || item.id : item))
      : []

    const requiredIds = Array.isArray(formData.requiredMetadataTemplates)
      ? formData.requiredMetadataTemplates.map((item: any) => (typeof item === 'object' ? item.value || item.id : item))
      : []

    // Combine optional and required for metadataTemplates field (remove duplicates)
    const allMetadataTemplateIds = Array.from(new Set([...optionalIds, ...requiredIds]))

    const apiData: any = {
      name: formData.name,
      description: formData.description,
      requireExpirationDate: formData.requireExpirationDate,
      expirationTimeMonths: Number(formData.expirationTimeMonths) || 24,
      expirationTimeMonthsEnabled: formData.expirationTimeMonthsEnabled,
      effectiveTimeDaysEnabled: formData.effectiveTimeDaysEnabled,
      effectiveTimeDays: Number(formData.effectiveTimeDays) || 10,
      // Extract ID from templateFile object (handle both object and number)
      templateFileId: formData.templateFileId
        ? typeof formData.templateFileId === 'object'
          ? Number(formData.templateFileId.value || formData.templateFileId.id)
          : Number(formData.templateFileId)
        : null,
      autonamingEnabled: formData.autonamingEnabled,
      autonamingTemplate: formData.autonamingTemplate || '',
      autonamingSeqNo: Number(formData.autonamingSeqNo) || 0,
      addSignaturePage: !!formData.signatureTemplateFileId, // API expects addSignaturePage
      addSignaturePageToFront: formData.addSignaturePageToFront,
      // Extract ID from signatureTemplateFile object (handle both object and number)
      signatureTemplateFileId: formData.signatureTemplateFileId ? Number(formData.signatureTemplateFileId.value) : null,
      amendmentsEnabled: formData.amendmentsEnabled,
      requireJustification: formData.requireJustification,
      requireRejectReason: formData.requireRejectReason,
      // When 'custom' is selected, send the user-typed text; otherwise capitalize the dropdown value
      customEffectiveStateLabel:
        formData._customEffectiveStateLabelType === 'custom'
          ? formData._customEffectiveStateLabelText
          : formData._customEffectiveStateLabelType
            ? formData._customEffectiveStateLabelType.charAt(0).toUpperCase() +
              formData._customEffectiveStateLabelType.slice(1)
            : 'Effective',
      keepDraftVersionsAfterApproval: formData.keepDraftVersionsAfterApproval,
      allowPropertiesChangeDuringEdit: formData.allowPropertiesChangeDuringEdit,
      allowPropertiesChangeDuringReview: formData.allowPropertiesChangeDuringReview,
      // Extract ID from workflowTemplate object (handle both object and number)
      workflowTemplate: formData.workflowTemplate
        ? typeof formData.workflowTemplate === 'object'
          ? Number(formData.workflowTemplate.value || formData.workflowTemplate.id)
          : Number(formData.workflowTemplate)
        : null,
      allowOverridesDuringCR: formData.allowOverridesDuringCR,
      // Extract IDs from metadata template objects - send both fields
      requiredMetadataTemplates: requiredIds.map((id: any) => Number(id)),
      metadataTemplates: allMetadataTemplateIds.map((id: any) => Number(id)),
      // Extract IDs from watermark objects
      watermarkIds: Array.isArray(formData.watermarkIds)
        ? formData.watermarkIds.map((item: any) => Number(typeof item === 'object' ? item.value || item.id : item))
        : [],
      isPeriodicReviewCustomDateEnabled: formData.isPeriodicReviewCustomDateEnabled,
      // Extract IDs from custom date template objects
      customNextDateMetadataTemplate: formData.customNextDateMetadataTemplate
        ? typeof formData.customNextDateMetadataTemplate === 'object'
          ? Number(formData.customNextDateMetadataTemplate.value || formData.customNextDateMetadataTemplate.id)
          : Number(formData.customNextDateMetadataTemplate)
        : null,
      customLastDateMetadataTemplate: formData.customLastDateMetadataTemplate
        ? typeof formData.customLastDateMetadataTemplate === 'object'
          ? Number(formData.customLastDateMetadataTemplate.value || formData.customLastDateMetadataTemplate.id)
          : Number(formData.customLastDateMetadataTemplate)
        : null,
      allowBlindingStatusChange: formData.allowBlindingStatusChange,
      isInitiatorReviewerOverlapCheckEnabled: formData.isInitiatorReviewerOverlapCheckEnabled,
      dueDateEnabled: formData.dueDateEnabled,
      dueDateOffset: Number(formData.dueDateOffset) || 30,
      _customEffectiveStateLabelText: formData._customEffectiveStateLabelText || '',
      _customEffectiveStateLabelType: formData._customEffectiveStateLabelType || 'effective'
    }

    if (isEdit) {
      apiData.id = formData.id
    }

    return apiData
  }

  const createDocumentType = async (formData: any) => {
    const apiData = transformFormDataToApi(formData)
    return await documentTypesApiService.create(apiData)
  }

  const updateDocumentType = async (id: number, formData: any) => {
    const apiData = transformFormDataToApi(formData)
    return await documentTypesApiService.update(id, apiData)
  }

  const doSave = async (docTypeValues: any) => {
    try {
      let apiCall: Promise<any>

      if (isEdit) {
        apiCall = updateDocumentType(docTypeValues.id, docTypeValues)
      } else {
        apiCall = createDocumentType(docTypeValues)
      }

      setLoading(apiCall)

      await apiCall

      toast.success(translate(isEdit ? 'DOCUMENT_TYPES_UPDATE_SUCCESS_MSG' : 'DOCUMENT_TYPES_CREATE_SUCCESS_MSG'))

      // Refresh the table
      if (tableContext?.refreshTable) {
        tableContext.refreshTable()
      }

      if (onSuccess) {
        onSuccess()
      }

      doClose()
    } catch (error: any) {
      // ApiError has body property, not data
      let errorMsg = translate('ERROR')
      if (error?.body) {
        // Try different error message formats from API
        errorMsg = error.body.message || error.body.error || error.body.errorMessage || JSON.stringify(error.body)
      } else if (error?.message) {
        errorMsg = error.message
      }

      toast.error(errorMsg)
    } finally {
      setLoading(null)
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate(isEdit ? 'SETTINGS_EDIT_TYPE' : 'SETTINGS_ADD_TYPE')}
      size="md"
      dataTestId="edit-doctype-dialog"
    >
      <EditForm
        onSubmit={doSave}
        doClose={doClose}
        isEdit={isEdit}
        initialValues={documentTypeData}
        loading={loading}
        isLoadingData={isLoadingData}
        templatesFiles={templatesFiles}
        availableSignatureForms={availableSignatureForms}
      />
    </Modal>
  )
}

export default ModalEdit
